import React from 'react';
import clsx from 'clsx';
import { AccidentItemHSPProps, COUNTRY_FLAGS } from './AccidentItemHSP.types';
import { SVGIcon } from '../SVGIcon/SVGIcon';
import { PhotoGallery } from '../PhotoGallery/PhotoGallery';
import damageImg from '@/assets/damages.png';
/**
 * Komponent wyświetlający informacje o szkodzie pojazdu w ramach historii szkód pojazdu (HSP)
 *
 * @example
 * ```tsx
 * <AccidentItemHSP
 *   date="14.05.2017"
 *   damageValue="6000 - 7000 PLN"
 *   damageCountry="Wielka Brytania"
 *   damageType="uszkodzenie_mechaniczne"
 *   damageLocation="Lewy, przedni bok"
 *   mileage="85 200 km"
 *   description="Do tej sekcji uszkodzeń kwalifikują się zarówno elementy odejmowalne nadwozia..."
 * />
 * ```
 */
export const AccidentItemHSP: React.FC<AccidentItemHSPProps> = ({
  date,
  damageValueMin,
  damageValueMax,
  damageCountry,
  damageType,
  damageLocation,
  mileage,
  description,
  damageGroups,
  currency,
  className,
  positionsIds,
  photoGallery,
  flagUrl,
  i18n,
  fallbackImages,
}) => {
  const countryInfo = damageCountry ? COUNTRY_FLAGS[damageCountry] : null;
  // const accidentTypeLabel = damageType
  //   ? ACCIDENT_TYPE_LABELS[damageType]
  //   : null;

  // i18n fallbacks
  const damageReportedTitle =
    i18n?.damageReportedTitle || 'Odnotowano szkodę pojazdu';
  const damageValueLabel = i18n?.damageValueLabel || 'Wartość szkody';
  const damageCountryLabel = i18n?.damageCountryLabel || 'Kraj szkody';
  const eventDetailsTitle = i18n?.eventDetailsTitle || 'Szczegóły zdarzenia:';
  const damagedElementsLabel =
    i18n?.damagedElementsLabel || 'Elementy które mogły ulec uszkodzeniu:';
  const damageLocationLabel = i18n?.damageLocationLabel || 'Miejsce szkody:';
  const mileageLabel =
    i18n?.mileageLabel || 'Przebieg przy wystąpieniu zdarzenia';
  const kmUnit = i18n?.kmUnit || 'km';
  const photoGalleryTitle = i18n?.photoGalleryTitle || 'Zdjecia szkód pojazdu';
  const flagAltTemplate = i18n?.flagAltTemplate || 'Flaga {country}';
  const flagAlt = flagAltTemplate.replace(
    '{country}',
    countryInfo?.name || damageCountry || '',
  );

  return (
    <div>
      <div
        className={clsx(
          'relative flex flex-col items-start gap-4 lg:flex-row pb-4',
          (positionsIds?.length && positionsIds.length > 0) || (fallbackImages?.length && fallbackImages.length > 0) ? 'mb-0' : 'mb-10',
          className,
        )}
      >
        {
          (positionsIds?.length && positionsIds.length > 0) || (fallbackImages?.length && fallbackImages.length > 0) ? (
            positionsIds?.length && positionsIds.length > 0 ? (
              <div
                className={clsx(
                  'grid w-full gap-4 lg:w-1/2',
                  positionsIds?.length && positionsIds.length > 1
                    ? 'grid-cols-2'
                    : 'grid-cols-1',
                )}
              >
                {positionsIds?.map((id) => (
                  <img
                    src={`/images-raport/damage-points/${id}.png`}
                    alt="Damage"
                    className="w-auto h-auto"
                  />
                ))}
              </div>
            ) : (
              <>
                {fallbackImages?.map((i) => (
                  <div>
                    <div className="relative">
                      {i.pointsImages.length > 0 ? (
                        <>
                          <img
                            src={`/images-raport/${i.mainImage}`}
                            width={500}
                            height={500}
                            alt=""
                          />
                          {i.pointsImages.map((image) => (
                            <div className="absolute top-0 left-0">
                              <img
                                src={`/images-raport/${image}`}
                                width={500}
                                height={500}
                                alt=""
                              />
                            </div>
                          ))}
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                ))}
              </>
            )
          ) : (
            <></>
          )
        }
        <div
          className={clsx('w-full rounded-[15px] border border-grayBorder', {
            'lg:mx-4 lg:w-1/2 lg:max-w-[620px]':
              positionsIds?.length && positionsIds.length > 0,
            'mx-auto lg:max-w-[750px] xl:max-w-[850px] 2xl:max-w-[900px] lg:w-full':
              !positionsIds?.length || positionsIds.length === 0,
          })}
        >
          {/* Nagłówek */}
          <div className="relative rounded-t-[15px] bg-text-primary px-5 py-[18px] ">
            <div className="absolute right-4 top-1/2 w-[100px] translate-y-[-50%] md:w-[130px] xl:right-6 xl:w-[170px]">
              <img src={damageImg} className="object-cover w-full h-full" />
            </div>
            <div className="flex flex-col gap-1 items-start w-1/2 text-left md:w-full">
              <h3 className="font-inter text-lg font-semibold leading-[1.17] text-white">
                {damageReportedTitle}
              </h3>
              <time className="text-sm font-medium font-inter text-primary-400">
                {date}
              </time>
            </div>
          </div>

          {/* Pierwsza sekcja - Wartość i kraj szkody */}
          {(damageValueMin || damageValueMax || damageCountry) && (
            <div className="border-b border-gray-300 px-[23px] py-7 bg-white">
              <div className="flex flex-wrap justify-between gap-4 lg:gap-[20px]">
                {/* Wartość szkody */}
                {damageValueMin && damageValueMax && (
                  <div className="flex gap-[14px]">
                    <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-primary-100 text-[10px]">
                      <SVGIcon name="ruler-measure" fill="primary" />
                    </div>
                    <div className="flex w-[138px] flex-col items-start gap-1">
                      <span className="text-sm font-inter text-grayText">
                        {damageValueLabel}
                      </span>
                      <span className="text-base font-semibold leading-5 text-left font-inter text-text-primary">
                        {damageValueMin} - {damageValueMax} {currency}
                      </span>
                    </div>
                  </div>
                )}

                {/* Kraj szkody */}
                {damageCountry && countryInfo && (
                  <div className="flex gap-7 items-center">
                    <div className="flex items-center gap-[14px]">
                      <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-primary-100 p-1">
                        <SVGIcon name="location-pin" fill="blue" />
                      </div>
                      <div className="flex flex-col gap-1 items-start">
                        <span className="text-sm font-inter text-grayText">
                          {damageCountryLabel}
                        </span>
                        <span className="text-base font-semibold leading-5 font-inter text-text-primary">
                          {damageCountry}
                        </span>
                      </div>
                    </div>
                    {/* Flaga kraju */}
                    {flagUrl ? (
                      <div className="flex overflow-hidden justify-center items-center w-9 h-9">
                        <img
                          src={flagUrl}
                          alt={flagAlt}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Szczegóły zdarzenia */}
          {(damageType || damageLocation) && (
            <div className="border-b border-gray-300 px-[23px] py-7 bg-white rounded-br-[13px] rounded-bl-[13px] bg-white">
              <h4 className="mb-7 text-xl font-bold text-left font-inter text-text-primary">
                {eventDetailsTitle}
              </h4>

              <div className="flex flex-wrap flex-1 gap-10 justify-between w-full">
                {/* Rodzaj szkody */}
                {damageGroups?.length ? (
                  <div className="flex w-1/2 items-center gap-[14px]">
                    {/* <div className="relative w-10 h-10">
                    <div className="w-10 h-10 rounded-full bg-primary/20"></div>
                    <div className="flex absolute top-1 left-1 justify-center items-center w-8 h-8 rounded-full bg-primary">
                      <span className="text-sm font-extrabold text-white font-inter">
                        02
                      </span>
                    </div>
                  </div> */}
                    <div className="flex flex-col gap-1 items-start w-full">
                      <span className="text-sm text-left font-inter text-grayText">
                        {damagedElementsLabel}
                      </span>
                      <ul className="text-left font-inter text-sm font-semibold leading-[1.43] text-primary">
                        {damageGroups.map((group) => (
                          <li key={group}>{group}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : (
                  <></>
                )}

                {/* Miejsce szkody */}
                {damageLocation && (
                  <div className="flex w-1/2 flex-1 items-center gap-[14px]">
                    <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-red-200 p-1">
                      <div className="relative h-[22px] w-[22px]">
                        {/* Ikona samochodu */}
                        <div className="relative w-full h-full">
                          <SVGIcon name="car-accident" fill="red-800" />
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 w-full text-left">
                      <span className="text-sm font-inter text-grayText">
                        {damageLocationLabel}
                      </span>
                      <ul className="text-left font-inter text-sm font-semibold leading-[1.43] text-red-800">
                        {damageLocation.map((location) => (
                          <li key={location}>{location}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Przebieg */}
          {mileage && mileage !== '0' && (
            <div className="border-b border-gray-300 px-[23px] py-[15px] rounded-br-[13px] rounded-bl-[13px] bg-white">
              <div className="flex flex-wrap gap-4 items-center">
                <span className="font-inter text-sm font-semibold leading-[1.14] text-text-primary">
                  {mileageLabel}
                </span>
                <div className="leading-1 rounded-lg bg-text-primary px-[14px] py-[6px] font-inter text-[11px] font-medium uppercase text-white">
                  {mileage} {kmUnit}
                </div>
              </div>
            </div>
          )}

          {/* Opis */}
          {description && (
            <div className="rounded-b-[14px] bg-primary-200 px-[23px] py-6 text-left">
              <p className="text-justify font-inter text-[13px] leading-[1.85] text-text-muted">
                {description}
              </p>
            </div>
          )}
        </div>
      </div >
      {
        photoGallery && (
          <div className="mx-auto mt-10 w-[calc(100%-40px)]">
            <PhotoGallery
              photos={photoGallery.photos}
              title={photoGalleryTitle}
              collapsible={true}
              defaultExpanded={false}
              previewCount={4}
              galleryVariant="damage-hsp"
              i18n={{
                closeGalleryText: i18n?.closeGalleryText,
                openGalleryText: i18n?.openGalleryText,
              }}
            />
          </div>
        )
      }
    </div >
  );
};
